import type { CSSProperties } from "react";
import { RuleboundMark } from "@/components/RuleboundMark";

export type CoverProps = {
  title: string;
  author?: string;
  kicker?: string;
  rules?: number | null;
  edition?: string | null;
  cloth?: string;
  accent?: string;
  ink?: string;
  progress?: number;
  artSrc?: string | null;
  /** Compact variant for tiny covers (search rows, thumbnails): accent bar + title only. */
  mini?: boolean;
  className?: string;
  style?: CSSProperties;
};

const TEXTURE =
  "repeating-linear-gradient(90deg, rgba(255,255,255,.028) 0 1px, transparent 1px 3px), repeating-linear-gradient(0deg, rgba(0,0,0,.05) 0 1px, transparent 1px 3px)";

/**
 * Book cover — faithful port of the prototype `Cover.dc.html`.
 * Cloth variant when no art; image variant with gradient overlay + title when art present.
 */
export function Cover({
  title,
  author,
  kicker,
  rules,
  edition,
  cloth = "#29483D",
  accent = "#B86F44",
  ink = "#FCFAF5",
  progress = 0,
  artSrc,
  mini = false,
  className,
  style,
}: CoverProps) {
  const dim = ink === "#202522" ? "rgba(32,37,34,.55)" : "rgba(252,250,245,.62)";
  const hasArt = !!artSrc;
  const showBookmark = !mini && progress > 0;
  const meta = [rules != null ? `${rules} rules` : null, edition || null]
    .filter(Boolean)
    .join("  ·  ");

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "43 / 60",
        borderRadius: "3px 5px 5px 3px",
        boxShadow:
          "0 1px 1px rgba(32,37,34,.12), 0 14px 26px -14px rgba(32,37,34,.42), inset 0 0 0 1px rgba(0,0,0,.06)",
        overflow: "hidden",
        background: cloth,
        containerType: "inline-size",
        ...style,
      }}
    >
      {mini ? (
        hasArt ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={artSrc!}
            alt=""
            loading="lazy"
            decoding="async"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              padding: "9px 6px 6px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              background: TEXTURE,
              color: ink,
            }}
          >
            <div style={{ height: 2, width: 16, background: accent, borderRadius: 2, flex: "none" }} />
            <div
              style={{
                fontFamily: "var(--font-serif), serif",
                fontSize: "clamp(9px, 17cqw, 11px)",
                lineHeight: 1.18,
                fontWeight: 500,
                letterSpacing: "-.01em",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 4,
                overflow: "hidden",
              }}
            >
              {title}
            </div>
          </div>
        )
      ) : hasArt ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={artSrc!}
            alt=""
            loading="lazy"
            decoding="async"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              pointerEvents: "none",
              background:
                "linear-gradient(to top, rgba(18,22,18,.86) 0%, rgba(18,22,18,.34) 44%, rgba(18,22,18,0) 70%)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              pointerEvents: "none",
              padding: "16px 16px 15px 22px",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              color: "#FCFAF5",
            }}
          >
            {kicker && (
              <div style={{ fontSize: 9, letterSpacing: ".16em", textTransform: "uppercase", fontWeight: 700, color: accent, marginBottom: 2 }}>
                {kicker}
              </div>
            )}
            <div style={{ fontFamily: "var(--font-serif), serif", fontSize: "clamp(13px,12.5cqw,18px)", lineHeight: 1.12, fontWeight: 600, letterSpacing: "-.01em", textShadow: "0 1px 8px rgba(0,0,0,.4)", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }}>
              {title}
            </div>
            {author && <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.88, marginTop: 2 }}>{`by ${author}`}</div>}
          </div>
        </>
      ) : (
        <div
          style={{
            position: "absolute",
            inset: 0,
            padding: "clamp(12px,10cqw,18px) clamp(10px,9cqw,16px) clamp(11px,9cqw,16px) clamp(13px,13cqw,22px)",
            display: "flex",
            flexDirection: "column",
            background: TEXTURE,
            color: ink,
          }}
        >
          <div style={{ height: 2, width: "min(34px,20cqw)", background: accent, marginBottom: "clamp(7px,8cqw,13px)", borderRadius: 2 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minHeight: 0, overflow: "hidden" }}>
            {kicker && (
              <div style={{ fontSize: "clamp(7px,6cqw,9px)", letterSpacing: ".16em", textTransform: "uppercase", fontWeight: 700, color: accent }}>
                {kicker}
              </div>
            )}
            <div style={{ fontFamily: "var(--font-serif), serif", fontSize: "clamp(12px,12.5cqw,20px)", lineHeight: 1.12, fontWeight: 500, letterSpacing: "-.01em", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4, overflow: "hidden" }}>
              {title}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            <div style={{ height: 1, width: "100%", background: ink === "#202522" ? "rgba(32,37,34,.14)" : "rgba(255,255,255,.16)" }} />
            <div style={{ display: "flex", alignItems: "center", gap: "clamp(5px,4.5cqw,8px)" }}>
              {/* Publisher stamp (LOGO-HANDOFF §5): the containerless mark at
                  60%, bottom-left. Hidden below ~110px of cover, where 20px of
                  mark would crowd the author line and fall under the 24px
                  minimum UI size (§6). */}
              <span className="rb-cover-stamp" style={{ display: "flex", opacity: 0.6, flex: "none" }}>
                <RuleboundMark
                  size={20}
                  variant={ink === "#202522" ? "bare" : "bare-reverse"}
                  decorative
                />
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                {author && <div style={{ fontSize: "clamp(8px,7.5cqw,11px)", fontWeight: 600, letterSpacing: ".01em", color: ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{`by ${author}`}</div>}
                {meta && <div style={{ fontSize: "clamp(7px,6.3cqw,9.5px)", letterSpacing: ".04em", color: dim, fontWeight: 500 }}>{meta}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spine */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: mini ? 7 : 11,
          background: "linear-gradient(90deg, rgba(0,0,0,.30), rgba(0,0,0,.04) 55%, rgba(255,255,255,.10))",
          zIndex: 3,
          pointerEvents: "none",
        }}
      />

      {/* Progress bookmark ribbon */}
      {showBookmark && (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 20,
            width: 10,
            height: `${14 + progress * 62}%`,
            background: accent,
            zIndex: 4,
            boxShadow: "0 2px 4px rgba(32,37,34,.3)",
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 84%, 0 100%)",
          }}
        />
      )}
    </div>
  );
}
